import { Container, Row, Col, Alert, Button } from "react-bootstrap";
import RecordingTable from "../components/dashboard/RecordingTable";
import RecordingTableSkeleton from "../components/dashboard/RecordingTableSkeleton";
import RecordingSearch from "../components/dashboard/RecordingSearch";
import ExportDialog from "../components/dashboard/ExportDialog";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Recording } from "../components/dashboard/interfaces/Recording";
import { currentUserIsAdmin } from "../firebase";
import CometSpinner from "../components/CometSpinner";
import SessionPagination from "../components/sessions/SessionPagination";
import { useRecordingsCache } from "../hooks/useRecordingsCache";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";


export default function RecordingsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [recordingData, setRecordingData] = useState<Recording[] | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordingsPerPage] = useState(10); // Removed setRecordingsPerPage as it's not used
  const [totalRecordings, setTotalRecordings] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showExportDialog, setShowExportDialog] = useState(false);
  
  const { fetchRecordings, invalidateCache, isLoading, error } = useRecordingsCache();

  const loadRecordings = useCallback(async ({ userId, limit, offset }: { userId: string, limit: number, offset: number }) => {
    try {
      const result = await fetchRecordings({ userId, limit, offset });
      setRecordingData(result.recordings);
      setTotalRecordings(result.total);
      
      if (!result.fromCache) {
        console.log('Fresh data loaded from API');
      }
    } catch (error) {
      console.error('Failed to load recordings:', error);
      // Keep existing data if available
    } finally {
      setIsInitialLoad(false);
    }
  }, [fetchRecordings]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    const newOffset = (page - 1) * recordingsPerPage;
    // Clear current data to show loading state
    setRecordingData(null);
    loadRecordings({ 
      userId: userId ?? "", 
      limit: recordingsPerPage, 
      offset: newOffset 
    });
  }, [userId, recordingsPerPage, loadRecordings]);

  useEffect(() => {
    if (!userId) {
      navigate("/404");
      return;
    }
    
    // Load initial data
    loadRecordings({ 
      userId: userId, 
      limit: recordingsPerPage, 
      offset: 0 
    });
    
    // Check admin status
    currentUserIsAdmin().then((isAdmin) => {
      setIsAdmin(isAdmin);
    });
  }, [userId, navigate, recordingsPerPage, loadRecordings]);
  
  // Filter recordings based on search term
  const filteredRecordings = useMemo(() => {
    if (!recordingData || !searchTerm.trim()) {
      return recordingData;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return recordingData.filter(recording => 
      recording.name.toLowerCase().includes(searchLower) ||
      recording.recordingId.toLowerCase().includes(searchLower) ||
      new Date(recording.timestamp).toLocaleString('de-DE').includes(searchLower)
    );
  }, [recordingData, searchTerm]);
  
  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleRefresh = useCallback(() => {
    if (userId) {
      invalidateCache(userId);
      setCurrentPage(1);
      loadRecordings({ 
        userId: userId, 
        limit: recordingsPerPage, 
        offset: 0 
      });
    }
  }, [userId, invalidateCache, loadRecordings, recordingsPerPage]);
  
  const spinnerContainer = useMemo(() => (
    <Container
      style={{
        backgroundColor: "#091014",
        minHeight: "100vh",
        minWidth: "100vw",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CometSpinner height={100} />
    </Container>
  ), []);
  
  const errorAlert = useMemo(() => (
    <Alert variant="danger" className="m-3">
      <Alert.Heading>Error Loading Recordings</Alert.Heading>
      <p>{error}</p>
      <Button variant="outline-danger" onClick={handleRefresh}>
        Try Again
      </Button>
    </Alert>
  ), [error, handleRefresh]);

  // Show spinner only on initial load
  if (isInitialLoad && recordingData === null) {
    return spinnerContainer;
  }

  return (
    <Container
      fluid
      style={{ backgroundColor: "#091014", minHeight: "100vh", color: "#fff" }}
    >
      <Row className="pb-3 pt-3 text-center">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div></div>
            <h1>Recordings</h1>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => setShowExportDialog(true)}
                disabled={isLoading}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-1" />
                Export Data
              </Button>
              <Button 
                variant="outline-light" 
                size="sm" 
                onClick={handleRefresh}
                disabled={isLoading}
              >
                {isLoading && !recordingData ? 'Loading...' : isLoading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
      {error && errorAlert}
      
      {/* Search functionality */}
      {recordingData && recordingData.length > 0 && (
        <Row className="mb-3">
          <Col md={6} lg={4}>
            <RecordingSearch
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              disabled={isLoading}
            />
          </Col>
          {searchTerm && (
            <Col className="d-flex align-items-center">
              <small className="text-muted">
                Showing {filteredRecordings ? filteredRecordings.length : 0} of {recordingData.length} recordings
              </small>
            </Col>
          )}
        </Row>
      )}
      <Row>
        <Col>
          {!recordingData ? (
            <RecordingTableSkeleton rows={recordingsPerPage} />
          ) : filteredRecordings && filteredRecordings.length > 0 ? (
            <RecordingTable
              userId={userId}
              recordings={filteredRecordings}
              onBack={() => navigate(-1)}
              isAdmin={isAdmin}
            />
          ) : recordingData && recordingData.length > 0 && searchTerm ? (
            <Container className="text-center py-5">
              <h3>No recordings match your search</h3>
              <p className="text-muted">Try adjusting your search terms or clear the search to see all recordings.</p>
              <Button variant="outline-light" onClick={() => setSearchTerm("")}>
                Clear Search
              </Button>
            </Container>
          ) : (
            !isLoading && (
              <Container className="text-center py-5">
                <h3>No recordings found</h3>
                <p className="text-muted">There are no recordings available for this user.</p>
                <Button variant="outline-light" onClick={handleRefresh}>
                  Refresh
                </Button>
              </Container>
            )
          )}
        </Col>
      </Row>
      {recordingData && recordingData.length > 0 && !searchTerm && (
        <Row className="mt-4 justify-content-center">
          <Col xs="auto">
            {totalRecordings > recordingsPerPage && (
              <SessionPagination
                activePage={currentPage}
                totalSessions={totalRecordings}
                sessionsPerPage={recordingsPerPage}
                onPageChange={handlePageChange}
              />
            )}
          </Col>
        </Row>
      )}
      
      {searchTerm && filteredRecordings && filteredRecordings.length > 0 && (
        <Row className="mt-4 justify-content-center">
          <Col xs="auto">
            <small className="text-muted">
              Pagination is disabled during search. Clear search to enable pagination.
            </small>
          </Col>
        </Row>
      )}
      
      <ExportDialog
        show={showExportDialog}
        onHide={() => setShowExportDialog(false)}
        userId={userId}
      />
    </Container>
  );
}
